import { useCallback, useEffect, useMemo } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { Avatar } from '@socialchorus/shared-ui-components';
import Page, { PageMain, PageSidebar } from '../../components/ui/page';
import { Spinner } from '../../components/ui';
import Feed from '../../components/feed';
import useScreenTitle from '../../common/use-screen-title';
import { fetchAlias, fetchAliasContents } from '../../services/alias';
import { contentOperations } from '../../models/content';
import { Content } from '../../models/content/types';
import NotFound from '../not-found';

import './alias-profile.scss';

type AliasProfileParams = {
  aliasId: string;
};

// Retry on network errors and 5xx, but treat 4xx as terminal so NotFound renders
// promptly without waiting for 3 exponential-backoff retries (~7s).
const retryExceptClientErrors = (failureCount: number, error: unknown) => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (status && status >= 400 && status < 500) return false;
  }
  return failureCount < 3;
};

function AliasProfile({ match }: RouteComponentProps<AliasProfileParams>) {
  const aliasId = match.params.aliasId;
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const aliasQuery = useQuery({
    queryKey: ['aliasProfile', aliasId],
    queryFn: async () => {
      const response = await fetchAlias(aliasId);
      return response.data.data;
    },
    retry: retryExceptClientErrors,
  });

  const contentsQuery = useInfiniteQuery({
    queryKey: ['aliasContents', aliasId],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await fetchAliasContents(
        aliasId,
        pageParam as number,
        20
      );
      return {
        items: response.data.contents || [],
        meta: response.data.meta,
      };
    },
    getNextPageParam: (lastPage) => {
      const pageSize = lastPage.meta?.page?.size ?? 0;
      const pageNumber = lastPage.meta?.page?.number ?? 0;
      return lastPage.items.length >= pageSize && pageSize > 0
        ? pageNumber + 1
        : undefined;
    },
    retry: retryExceptClientErrors,
  });

  const feedItems: Content[] = useMemo(
    () =>
      contentsQuery.data?.pages.flatMap((p) =>
        p.items
          .map((fi) => fi.attributes)
          .filter(
            (fi): fi is Content =>
              fi != null &&
              Object.prototype.hasOwnProperty.call(fi, 'content_type')
          )
      ) ?? [],
    [contentsQuery.data]
  );

  // Populate Redux from the cached query data (not inside queryFn), so cache
  // hits that skip the queryFn still hydrate the store.
  useEffect(() => {
    if (feedItems.length > 0) {
      dispatch(contentOperations.addContents(feedItems));
    }
  }, [feedItems, dispatch]);

  const canLoadMore = contentsQuery.hasNextPage ?? false;
  const page = contentsQuery.data?.pages.length ?? 0;

  const fetchNextPage = useCallback(() => {
    if (canLoadMore && !contentsQuery.isFetchingNextPage) {
      contentsQuery.fetchNextPage();
    }
  }, [canLoadMore, contentsQuery]);

  const displayName = aliasQuery.data?.attributes?.display_name;
  useScreenTitle(displayName);

  if (aliasQuery.isError) return <NotFound />;

  if (aliasQuery.isLoading) {
    return <Spinner center />;
  }

  const alias = aliasQuery.data;
  if (!alias) return <NotFound />;

  const avatarUrl = alias.attributes.avatar_url;
  const description = alias.attributes.description;

  return (
    <Page className="alias-profile">
      <PageSidebar>
        <div className="alias-profile__sidebar">
          <div className="alias-profile__avatar">
            <Avatar
              size="large"
              shape="circle"
              imgSrc={avatarUrl || undefined}
            />
          </div>
          <h1 className="alias-profile__name text-heading-bold">
            {displayName}
          </h1>
          {description && (
            <p className="alias-profile__description text-body text-black-60">
              {description}
            </p>
          )}
        </div>
      </PageSidebar>
      <PageMain>
        <div className="alias-profile__content">
          <Feed
            items={feedItems}
            page={page}
            fetching={
              contentsQuery.isLoading || contentsQuery.isFetchingNextPage
            }
            titleHeadingLevel={2}
            canLoadMore={canLoadMore}
            fetchFeed={fetchNextPage}
            analyticsData={{ location: 'alias_profile' }}
          />
        </div>
      </PageMain>
    </Page>
  );
}

export default AliasProfile;
